import { useEffect, useState } from 'react';

import type { ApiAreaOfFocus } from '#/api/types/ApiAreaOfFocus';
import type { ApiJobFunction } from '#/api/types/ApiJobFunction';
import { useAppConfig } from '#/config/AppConfigContext';
import { searchAlgoliaFetchRecommendedJobs } from '#/modules/search/algolia/fetch/searchAlgoliaFetchRecommendedJobs';
import type { SearchAlgoliaRecommendedListingHit } from '#/modules/search/algolia/types/SearchAlgoliaRecommendedListingHit';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import { log } from '#/utils/logging';

/*
 * Fetch recommended jobs
 */
export function useSearchAlgoliaRecommendedJobs(
  searchLocation: Pick<SearchLocation, 'latitude' | 'longitude'> &
    Partial<Omit<SearchLocation, 'latitude' | 'longitude'>>,
  options?: {
    onlyWithLogo: boolean;
    jobFunction?: ApiJobFunction | null;
    areaOfFocus?: ApiAreaOfFocus | null;
    jobTitle?: string;
    familyId?: string;
    locale?: string;
    query?: string;
  },
) {
  // @ts-expect-error TS(2339): Property 'onlyWithLogo' does not exist on type '{ ... Remove this comment to see the full error message
  const { onlyWithLogo, jobFunction, areaOfFocus, locale, familyId } = options;

  const { appConfig } = useAppConfig();
  const [data, setData] = useState<SearchAlgoliaRecommendedListingHit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    searchAlgoliaFetchRecommendedJobs({
      searchLocation,
      options: {
        onlyWithLogo,
        jobFunction,
        areaOfFocus,
        locale,
        familyId,
      },
      searchOptions: { distinct: true },
      appConfig,
    })
      .then((hits) => {
        setData(hits);
      })
      .catch((e) => {
        setError(e);
        log.error(e);
      })
      .finally(() => setIsLoading(false));
  }, [
    searchLocation,
    onlyWithLogo,
    jobFunction,
    areaOfFocus,
    locale,
    familyId,
    appConfig,
  ]);

  return { data, isLoading, error };
}
